import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { isApiErrorMessage } from 'src/api/utils/is-api-error-message';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { spaceGroupsApiErrorsTranslations } from '../constants/api-errors-translations';
import { updateSpaceGroupName } from '../lib/space-groups';
import { spaceGroupsKeys } from '../query-keys/space-groups';
import { ApiErrorMessage } from '../types/api-error-message';

export const useUpdateSpaceGroupName = (onSuccess?: () => void) => {
  const { t } = useTranslation(['spaceGroups', 'common']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ token, name }: { token: string; name: string }) =>
      updateSpaceGroupName(token, name),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({
          queryKey: spaceGroupsKeys.lists(),
        });

        queryClient.invalidateQueries({
          queryKey: spaceGroupsKeys.details(),
        });
      });
      toast.success(t('spaceGroups:common.editNameDialog.success'));
      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          spaceGroupsApiErrorsTranslations,
        )
      ) {
        toast.error(t(spaceGroupsApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(errorMessage || t('common:errorMessages.somethingWentWrong'));
    },
  });
};
